import React, { useState, useEffect, useCallback } from "react";
import MainLayout from "../Themes/MainLayout";
import {
  Typography,
  Table,
  MenuItem,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  TableContainer,
  Paper,
  Button,
  CircularProgress,
  Container,
  Alert,
  TextField,
  CssBaseline,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { ThemeProvider } from "@mui/material/styles";
import Table_theme from "../Themes/TableTheme";
import { useCurrentUser } from "../utils/useCurrentUser";
import axios from "axios";
import { Autocomplete } from "@mui/material";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const apiBaseUrl = `${BASE_URL}/api/leave-request`;

const LeaveApproval = () => {
  const { userId, empId, boId, roles, hasPermission } = useCurrentUser();
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [alert, setAlert] = useState({ type: "", msg: "" });
  const [userRole, setUserRole] = useState(null); // Initialize as null to track unset state
  const [resourcesLoading, setResourcesLoading] = useState(false);
  const [projectResources, setProjectResources] = useState([]);
  const [projectResourcesMap, setProjectResourcesMap] = useState({});

  useEffect(() => {
    const initialize = async () => {
      setLoading(true);
      try {
        const role = await fetchClientProjects(empId);

        if (role) {
          setUserRole(role);
        } else {
          setError("User has no role in any project");
        }
      } catch (error) {
        setError("Error fetching user role");
      } finally {
        setLoading(false);
      }
    };
    initialize();
  }, []);

  useEffect(() => {
    if (userRole) {
      fetchApprovals();
    }
  }, [userRole]);

  const fetchClientProjects = async (currentUserId) => {
    try {
      const params = new URLSearchParams({
        page: 1,
        limit: 100,
        boId,
        empId,
        roles,
      });
      const response = await fetch(
        `${BASE_URL}/clientprojects/paginated?${params.toString()}`
      );
      const result = await response.json();
      const userProjects = result.data.filter(
        (project) =>
          project.projectmanagerid === currentUserId ||
          project.technicalleadid === currentUserId ||
          project.hrid === currentUserId
      );
      if (userProjects.length === 0) return null;

      const firstMatch = userProjects[0];
      if (firstMatch.projectmanagerid === currentUserId) {
        return "Project Manager";
      } else if (firstMatch.technicalleadid === currentUserId) {
        return "Technical Lead";
      } else if (firstMatch.hrid === currentUserId) {
        return "HR";
      }

      return null;
    } catch (error) {
      console.error("Error fetching client projects:", error);
      return null;
    }
  };

  const fetchApprovals = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${BASE_URL}/LeaveApproval/approvals/${userRole}/${empId}/${boId}`
      );
      if (!res.ok) throw new Error("Failed to fetch approvals");
      const data = await res.json();
      console.log("Approvals API data:", data);

      const mapped = data
        .filter((row) => row.approval_status.toLowerCase() !== "approved")
        .map((row) => ({
          ...row,
          full_name: `${row.first_name || "Unknown"} ${
            row.last_name || "Unknown"
          }`.trim(),
          approval_status: row.approval_status || row.status || "Pending",
          // Don't override to "", use whatever came from backend
          alternative_resource: row.alternative_resource,
        }));

      setApprovals(mapped);

      // Now fetch resources for both employee and alternative resource
      mapped.forEach((row) => {
        fetchProjectResourcesForEmployee(row.employee_id);
        if (row.alternative_resource) {
          fetchProjectResourcesForEmployee(row.alternative_resource);
        }
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRemarksChange = (index, remarks) => {
    setApprovals((prev) => {
      const updated = [...prev];
      updated[index].remarks = remarks;
      return updated;
    });
  };
  const fetchProjectResourcesForEmployee = useCallback(
    async (targetEmployeeId) => {
      try {
        if (!targetEmployeeId) return;

        setResourcesLoading(true);

        const res = await axios.get(
          `${apiBaseUrl}/project-resources/${targetEmployeeId}`,
          { params: { bo_id: boId } }
        );
        setProjectResourcesMap((prev) => ({
          ...prev,
          [targetEmployeeId]: res.data || [],
        }));
      } catch (err) {
        console.error("Error fetching resources:", err);
        setProjectResourcesMap((prev) => ({
          ...prev,
          [targetEmployeeId]: [],
        }));
      } finally {
        setResourcesLoading(false);
      }
    },
    [boId]
  );

  console.log("approvals:", approvals);
  console.log("projectResourcesMap:", projectResourcesMap);

  const getNameFromEmployeeId = (row) => {
    console.log("getNameFromEmployeeId-->", row);
    const resources = projectResourcesMap[row.employee_id] || []; // ✅ employee_id, not alternative_resource
    const match = resources.find(
      (item) => item.employee_id === row.alternative_resource
    );
    return match
      ? `${match.first_name} ${match.last_name}`
      : "No alternative resource selected";
  };
  const handleApproveTL = async (row) => {
    const targetStatus =
      row.approval_level_required === 1
        ? "Approved"
        : "Waiting for Manager approval";
    try {
      const res = await fetch(`${BASE_URL}/LeaveApproval/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          request_id: row.request_id,
          bo_id: row.bo_id,
          employee_id: row.employee_id,
          approver_id: empId,
          approval_status: targetStatus,
          remarks: row.remarks || "",
          approved_by: empId,
          userRole: "Technical Lead",
          alternative_resource: row.alternative_resource,
        }),
      });
      if (!res.ok)
        throw new Error(`Approval failed: ${res.status} ${res.statusText}`);
      setAlert({ type: "success", msg: "Leave approved by Technical Lead!" });
      setTimeout(() => setAlert({ type: "", msg: "" }), 3000);
      fetchApprovals();
    } catch (err) {
      setAlert({ type: "error", msg: `Error approving leave: ${err.message}` });
      console.error(err);
    }
  };
  const handleApprovePM = async (row) => {
    try {
      const res = await fetch(`${BASE_URL}/LeaveApproval/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          request_id: row.request_id,
          bo_id: row.bo_id,
          employee_id: row.employee_id,
          approver_id: empId,
          approval_status: "Approved",
          remarks: row.remarks || "",
          approved_by: empId,
          userRole: "Project Manager",
        }),
      });
      if (!res.ok)
        throw new Error(`Approval failed: ${res.status} ${res.statusText}`);
      setAlert({ type: "success", msg: "Leave approved by Project Manager!" });
      setTimeout(() => setAlert({ type: "", msg: "" }), 3000);
      fetchApprovals();
    } catch (err) {
      setAlert({ type: "error", msg: `Error approving leave: ${err.message}` });
      console.error(err);
    }
  };
  const handleApproveHR = async (row) => {
    try {
      const res = await fetch(`${BASE_URL}/LeaveApproval/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          request_id: row.request_id,
          bo_id: row.bo_id,
          employee_id: row.employee_id,
          approver_id: empId,
          approval_status: "Approved",
          remarks: row.remarks || "",
          approved_by: empId,
          userRole: "HR", // Level 3 Approver
        }),
      });
      if (!res.ok)
        throw new Error(`Approval failed: ${res.status} ${res.statusText}`);
      setAlert({ type: "success", msg: "Leave approved by HR!" });
      setTimeout(() => setAlert({ type: "", msg: "" }), 3000);
      fetchApprovals();
    } catch (err) {
      setAlert({ type: "error", msg: `Error approving leave: ${err.message}` });
      console.error(err);
    }
  };
  const handleReject = async (row) => {
    try {
      const res = await fetch(`${BASE_URL}/LeaveApproval/reject`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          request_id: row.request_id,
          bo_id: row.bo_id,
          employee_id: row.employee_id,
          approval_status: "Rejected",
          remarks: row.remarks,
          rejected_by: empId,
          userRole,
        }),
      });
      if (!res.ok) throw new Error("Rejection failed");
      setAlert({ type: "success", msg: "Leave rejected!" });
      setTimeout(() => setAlert({ type: "", msg: "" }), 3000);
      fetchApprovals();
    } catch (err) {
      setAlert({ type: "error", msg: `Error rejecting leave: ${err.message}` });
      console.error(err);
    }
  };

  if (loading) return <CircularProgress />;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <MainLayout>
      <Typography
        variant="h4"
        gutterBottom
        fontSize="25px"
        fontWeight="600"
      >
        Leave Approvals ({userRole})
      </Typography>
      {alert.msg && (
        <Alert severity={alert.type} sx={{ mb: 2 }}>
          {alert.msg}
        </Alert>
      )}
      {approvals.length === 0 && !loading && !error && (
        <Typography>No leaves pending approval.</Typography>
      )}
      {approvals.length > 0 && (
        <ThemeProvider theme={Table_theme}>
          <CssBaseline />
          <TableContainer
            component={Paper}
            sx={{ overflowX: "auto", width: "100%" }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Employee</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Projects</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Leave Type</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Total Days</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Alternative Resource</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Total Hours</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Submitted At</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Status</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Remarks</strong>
                  </TableCell>
                  <TableCell align="center">
                    <strong>Actions</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {approvals.map((row, index) => {
                  console.log("Row in TableBody:", row);
                  console.log(
                    "Alternative resource (mapped):",
                    row.alternative_resource
                  );
                  return (
                    <TableRow key={`${row.request_id}-${index}`}>
                      <TableCell>{row.full_name}</TableCell>
                      <TableCell>{row.proj_names}</TableCell>
                      <TableCell>{row.leave_type}</TableCell>
                      <TableCell>{row.total_days}</TableCell>
                      <TableCell>
                        {userRole === "Technical Lead" ? (
                          // SHOW DROPDOWN ONLY FOR TECHNICAL LEAD
                          <Autocomplete
                            size="small"
                            fullWidth
                            options={(
                              projectResourcesMap[row.employee_id] || []
                            ).map((item) => ({
                              label: `${item.first_name} ${item.last_name}`,
                              value: item.employee_id,
                            }))}
                            value={
                              (projectResourcesMap[row.employee_id] || [])
                                .map((item) => ({
                                  label: `${item.first_name} ${item.last_name}`,
                                  value: item.employee_id,
                                }))
                                .find(
                                  (opt) =>
                                    opt.value === row.alternative_resource
                                ) || null
                            }
                            onOpen={() =>
                              fetchProjectResourcesForEmployee(row.employee_id)
                            }
                            onChange={(e, newValue) => {
                              setApprovals((prev) =>
                                prev.map((r, i) => {
                                  if (i === index) {
                                    return {
                                      ...r,
                                      alternative_resource: newValue
                                        ? newValue.value
                                        : "",
                                      alternative_resource_name: newValue
                                        ? newValue.label
                                        : "",
                                    };
                                  }
                                  return r;
                                })
                              );
                            }}
                            slotProps={{
                              paper: {
                                sx: {
                                  "& .MuiAutocomplete-option": {
                                    fontSize: "11px",
                                    padding: "3px 6px",
                                    minHeight: "24px",
                                  },
                                },
                              },
                            }}
                            sx={{
                              "& .MuiAutocomplete-input": {
                                padding: "2px 4px !important",
                                fontSize: "12px",
                              },
                              "& .MuiInputLabel-root": {
                                fontSize: "12px",
                              },
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="--Select--"
                                InputProps={{
                                  ...params.InputProps,
                                  sx: { height: "28px", fontSize: "12px" },
                                }}
                              />
                            )}
                          />
                        ) : (
                          <>
                            {row.alternative_resource ? (
                              (() => {
                                console.log(
                                  "Calling getNameFromEmployeeId for:",
                                  row
                                );
                                return getNameFromEmployeeId(row);
                              })()
                            ) : (
                              <span style={{ fontSize: "12px", color: "gray" }}>
                                No alternative resource selected
                              </span>
                            )}
                          </>
                        )}
                      </TableCell>
                      <TableCell>{row.total_hours}</TableCell>
                      <TableCell>
                        {new Date(row.submitted_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell> {row.approval_status}</TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          value={row.remarks || ""}
                          onChange={(e) =>
                            handleRemarksChange(index, e.target.value)
                          }
                          placeholder="Add remarks"
                          variant="outlined"
                          InputProps={{
                            sx: {
                              width: "150px",
                              height: "30px", // Same as small button
                              fontSize: "0.85rem", // optional
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {row.status !== "Approved" &&
                          row.status !== "Rejected" && (
                            <>
                              {userRole === "Technical Lead" && (
                                <Button
                                  variant="contained"
                                  color="success"
                                  size="small"
                                  onClick={() => handleApproveTL(row)}
                                  startIcon={<CheckIcon />}
                                  sx={{ mr: 1, minWidth: "80px" }}
                                >
                                  Approve
                                </Button>
                              )}

                              {userRole === "Project Manager" && (
                                <Button
                                  variant="contained"
                                  color="success"
                                  size="small"
                                  onClick={() => handleApprovePM(row)}
                                  startIcon={<CheckIcon />}
                                  sx={{ mr: 1, minWidth: "80px" }}
                                >
                                  Approve
                                </Button>
                              )}

                              {userRole === "HR" && (
                                <Button
                                  variant="contained"
                                  color="success"
                                  size="small"
                                  onClick={() => handleApproveHR(row)}
                                  startIcon={<CheckIcon />}
                                  sx={{ mr: 1, minWidth: "80px" }}
                                >
                                  Approve
                                </Button>
                              )}

                              <Button
                                variant="contained"
                                color="error"
                                size="small"
                                onClick={() => handleReject(row)}
                                startIcon={<CloseIcon />}
                                sx={{ minWidth: "80px" }}
                              >
                                Reject
                              </Button>
                            </>
                          )}
                        {row.status === "Rejected" && (
                          <Typography color="error" variant="body2">
                            Rejected
                          </Typography>
                        )}
                        {row.status === "Approved" && (
                          <Typography color="green" variant="body2">
                            Approved
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </ThemeProvider>
      )}
    </MainLayout>
  );
};

export default LeaveApproval;
